/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { z } from 'zod';

/** Methods a payment can arrive by — mirrors the API `payment_method` enum. */
export const PAYMENT_METHODS = ['cash', 'card', 'upi', 'bank_transfer', 'razorpay'] as const;

/** Statuses an invoice can be raised in from the UI. */
export const INVOICE_DRAFT_STATUSES = ['draft', 'sent'] as const;

/** Money is paise (integer); 50 lakh cap mirrors the API DTO. */
const MAX_AMOUNT_PAISE = 5_000_000_00;

const amountPaise = z.coerce
  .number({ invalid_type_error: 'Enter an amount' })
  .int('Amount must be a whole number of paise')
  .min(1, 'Enter an amount')
  .max(MAX_AMOUNT_PAISE, 'Amount is too large');

/**
 * Raise an invoice (Figma Finance → "New Invoice"). Mirrors the API
 * `CreateInvoiceDto`. The FE form collects rupees and converts to paise before
 * validation so this canonical field stays in paise on both sides.
 */
export const CreateInvoiceSchema = z.object({
  clientId: z.string().uuid('Select a client'),
  amountPaise,
  dueDate: z.string().trim().max(10).optional(),
  notes: z.string().trim().max(500).optional(),
  status: z.enum(INVOICE_DRAFT_STATUSES).optional(),
  /** Catalogue item this invoice is priced from (P1-9); optional for ad-hoc bills. */
  serviceItemId: z.string().uuid().optional(),
});
export type CreateInvoiceInput = z.infer<typeof CreateInvoiceSchema>;

/**
 * Create / edit a service-catalogue item (BRD M10 — P1-9, Figma Finance →
 * Services). The FE collects rupees and converts to paise. Mirrors the API
 * `CreateServiceItemDto` / `UpdateServiceItemDto`.
 */
export const CreateServiceItemSchema = z.object({
  name: z.string().trim().min(2, 'Name is too short').max(120),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  unitPricePaise: amountPaise,
  active: z.boolean().optional(),
});
export type CreateServiceItemInput = z.infer<typeof CreateServiceItemSchema>;

/** Partial update — every field optional so the form can patch one at a time. */
export const UpdateServiceItemSchema = CreateServiceItemSchema.partial();
export type UpdateServiceItemInput = z.infer<typeof UpdateServiceItemSchema>;

/**
 * Payment modes a clinic can accept on an invoice (BRD v2.0 CR-010). The policy
 * list the admin toggles in Billing Settings and selects when recording a manual
 * payment; a superset of the gateway-oriented {@link PAYMENT_METHODS} minus
 * `razorpay` (the gateway path is server-driven, not manually selected).
 */
export const PAYMENT_MODES = [
  'cash',
  'upi',
  'card',
  'bank_transfer',
  'insurance',
  'cheque',
] as const;
export type PaymentMode = (typeof PAYMENT_MODES)[number];

/**
 * Record a payment against an invoice (Figma Finance → "Record Payment"). CR-010
 * captures the **mode** (from the clinic's accepted modes), an optional
 * **reference** (cheque no / UTR / txn id) and the **payment date**.
 */
export const RecordPaymentSchema = z.object({
  amountPaise,
  method: z.enum(PAYMENT_MODES),
  reference: z.string().trim().max(100).optional().or(z.literal('')),
  paymentDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Pick a date')
    .optional(),
});
export type RecordPaymentInput = z.infer<typeof RecordPaymentSchema>;

const pct = z.coerce.number().int('Enter a whole number').min(0).max(100);
const slaHours = z.coerce.number().int('Enter whole hours').min(1).max(8760);
const prefix = z
  .string()
  .trim()
  .regex(/^[A-Z0-9]{2,12}$/, 'Use 2–12 uppercase letters or digits');

/**
 * Per-clinic billing & rules settings (BRD v2.0 S2 foundation). Mirrors the API
 * `finance.clinic_billing_settings` row and the Billing Settings form. Money is
 * paise; pay rules are percentages (0–100); GST is basis points (1800 = 18%).
 */
export const BillingSettingsSchema = z.object({
  proformaDay: z.coerce.number().int().min(24).max(28),
  autoSendProforma: z.boolean(),
  autoConvertApproved: z.boolean(),
  invoicePrefix: prefix,
  invoiceNextSeq: z.coerce.number().int().min(1).max(99_999_999),
  proformaPrefix: prefix,
  gstRateBps: z.coerce.number().int().min(0).max(10_000),
  payPctCompleted: pct,
  payPctClientLate: pct,
  payPctConsultantAbsent: pct,
  payPctCancelled: pct,
  payPctNoShow: pct,
  acceptedPaymentModes: z.array(z.enum(PAYMENT_MODES)).min(1, 'Enable at least one mode'),
  noShowFeePaise: z.coerce.number().int().min(0).max(MAX_AMOUNT_PAISE),
  slaProformaDeclinedHours: slaHours,
  slaPatientQueryHours: slaHours,
  slaInvoiceOverdueHours: slaHours,
  slaConsultantInvoiceHours: slaHours,
});
export type BillingSettingsInput = z.infer<typeof BillingSettingsSchema>;

/** Consultant invoice lifecycle (BRD v2.0 CR-006); mirrors the API enum. */
export const CONSULTANT_INVOICE_STATUSES = [
  'draft',
  'submitted',
  'approved',
  'rejected',
  'paid',
] as const;
export type ConsultantInvoiceStatus = (typeof CONSULTANT_INVOICE_STATUSES)[number];

/** Generate a consultant invoice for a calendar month (`YYYY-MM`). */
export const GenerateConsultantInvoiceSchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Pick a month'),
});
export type GenerateConsultantInvoiceInput = z.infer<typeof GenerateConsultantInvoiceSchema>;

/** Consultant submits the generated invoice (optional remarks). */
export const SubmitConsultantInvoiceSchema = z.object({
  remarks: z.string().trim().max(500).optional().or(z.literal('')),
});
export type SubmitConsultantInvoiceInput = z.infer<typeof SubmitConsultantInvoiceSchema>;

/** Admin rejects a submitted invoice with a reason. */
export const RejectConsultantInvoiceSchema = z.object({
  note: z.string().trim().min(1, 'A reason is required').max(500),
});
export type RejectConsultantInvoiceInput = z.infer<typeof RejectConsultantInvoiceSchema>;

/** Admin records payment of an approved invoice. */
export const PayConsultantInvoiceSchema = z.object({
  paymentMode: z.enum(PAYMENT_MODES),
});
export type PayConsultantInvoiceInput = z.infer<typeof PayConsultantInvoiceSchema>;

/** Proforma lifecycle (BRD v2.0 CR-008/CR-009); mirrors the API enum. */
export const PROFORMA_STATUSES = ['draft', 'sent', 'approved', 'declined', 'converted'] as const;
export type ProformaStatus = (typeof PROFORMA_STATUSES)[number];

/**
 * Generate the proforma cycle for a billing month (`YYYY-MM`). One draft per
 * active patient with ≥1 scheduled session that month; each patient's per-session
 * fee is derived from their assigned therapist's rate, falling back to
 * `sessionFeePaise` when the patient has no rated therapist (CR-008).
 */
export const GenerateProformaCycleSchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Pick a month'),
  sessionFeePaise: amountPaise,
});
export type GenerateProformaCycleInput = z.infer<typeof GenerateProformaCycleSchema>;

/** Admin adjusts a draft proforma (sessions / per-session fee / discount / note). */
export const AdjustProformaSchema = z.object({
  sessionsCount: z.coerce.number().int().min(0).max(1000).optional(),
  sessionFeePaise: amountPaise.optional(),
  discountPaise: amountPaise.optional(),
  note: z.string().trim().max(500).optional().or(z.literal('')),
});
export type AdjustProformaInput = z.infer<typeof AdjustProformaSchema>;

/** Patient declines a sent proforma with a reason (raises an admin follow-up). */
export const DeclineProformaSchema = z.object({
  reason: z.string().trim().min(1, 'A reason is required').max(500),
});
export type DeclineProformaInput = z.infer<typeof DeclineProformaSchema>;

/** Net payment terms offered when converting a proforma to an invoice (CR-009). */
export const NET_DAYS_OPTIONS = [7, 15, 30] as const;

const invoiceDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Pick an invoice date');
const netDays = z.coerce
  .number()
  .int()
  .refine((n): n is (typeof NET_DAYS_OPTIONS)[number] => NET_DAYS_OPTIONS.includes(n as 7), {
    message: 'Choose a payment term',
  });

/**
 * Convert an approved proforma to a real invoice (CR-009). The admin picks the
 * invoice date (printed on the invoice) and the net payment term; the due date
 * is derived. Numbering (CLNK-YYYY-MM-NNNN) comes from the billing settings.
 */
export const ConvertProformaSchema = z.object({
  invoiceDate,
  netDays,
});
export type ConvertProformaInput = z.infer<typeof ConvertProformaSchema>;

/** Bulk-convert several approved proformas with one invoice date + term. */
export const BulkConvertProformasSchema = z.object({
  ids: z.array(z.string().uuid()).min(1, 'Select at least one proforma'),
  invoiceDate,
  netDays,
});
export type BulkConvertProformasInput = z.infer<typeof BulkConvertProformasSchema>;

/**
 * Issue a credit note against a paid (locked) invoice (BRD v2.0 CR-010). Paid
 * invoices are immutable; this is the only sanctioned correction path. The
 * amount cannot exceed the original invoice total (BE-guarded).
 */
export const IssueCreditNoteSchema = z.object({
  amountPaise,
  reason: z.string().trim().min(1, 'A reason is required').max(500),
});
export type IssueCreditNoteInput = z.infer<typeof IssueCreditNoteSchema>;

/**
 * Resolve the consultant pay percentage for a session outcome from the clinic's
 * billing settings (BRD v2.0 CR-005). Non-payable states (scheduled / in-progress)
 * return 0. Consumed by the consultant timesheet/invoice (S6/S7).
 */
export function payPctForSessionStatus(settings: BillingSettingsInput, status: string): number {
  switch (status) {
    case 'completed':
      return settings.payPctCompleted;
    case 'client_late':
      return settings.payPctClientLate;
    case 'consultant_attended_client_absent':
      return settings.payPctConsultantAbsent;
    case 'cancelled':
      return settings.payPctCancelled;
    case 'no_show':
      return settings.payPctNoShow;
    default:
      return 0;
  }
}

/** Defaults returned when a clinic has no settings row yet (shared FE + BE). */
export const BILLING_SETTINGS_DEFAULTS: BillingSettingsInput = {
  proformaDay: 25,
  autoSendProforma: false,
  autoConvertApproved: false,
  invoicePrefix: 'CLNK',
  invoiceNextSeq: 1,
  proformaPrefix: 'PF',
  gstRateBps: 0,
  payPctCompleted: 100,
  payPctClientLate: 50,
  payPctConsultantAbsent: 30,
  payPctCancelled: 0,
  payPctNoShow: 0,
  acceptedPaymentModes: ['cash', 'upi', 'card', 'bank_transfer', 'insurance', 'cheque'],
  noShowFeePaise: 0,
  slaProformaDeclinedHours: 48,
  slaPatientQueryHours: 24,
  slaInvoiceOverdueHours: 72,
  slaConsultantInvoiceHours: 120,
};
